import { useAppConfiguration, useGraphqlQuery } from '@mews-commander/data-access';
import { graphql, MatchedGuestProfilesResultQuery, PageInfo } from '@mews-commander/guest-profile';
import { useCurrentEnterprise } from '@mews-commander/legacy-entities/src/enterprise';

const MatchedGuestProfilesQueryDocument = graphql(`
    query MatchedGuestProfilesResult($chainId: UUID!, $first: Int, $after: String, $last: Int, $before: String) {
        matchedGuestProfiles(chainId: $chainId, first: $first, after: $after, last: $last, before: $before) {
            edges {
                cursor
                node {
                    id
                    guest1Id
                    guest2Id
                }
            }
            pageInfo {
                endCursor
                hasNextPage
                hasPreviousPage
                startCursor
            }
            totalCount
        }
    }
`);

interface MatchedGuestProfilesWithPagination {
    cursor: string;
    guestIds: Array<string>;
    pageInfo: PageInfo;
    totalMatchedProfiles: number;
}

const transformToMatchedGuestProfilesWithPagination = (
    data: MatchedGuestProfilesResultQuery
): MatchedGuestProfilesWithPagination | undefined => {
    const { matchedGuestProfiles: matchedGuestProfilesWithEdge } = data;

    if (
        !matchedGuestProfilesWithEdge ||
        !matchedGuestProfilesWithEdge.edges ||
        !matchedGuestProfilesWithEdge.pageInfo
    ) {
        return undefined;
    }

    const edge = matchedGuestProfilesWithEdge.edges[0];

    return {
        cursor: edge?.cursor,
        guestIds: edge?.node?.guest1Id && edge?.node?.guest2Id ? [edge.node.guest1Id, edge.node.guest2Id] : [],
        pageInfo: matchedGuestProfilesWithEdge.pageInfo,
        totalMatchedProfiles: matchedGuestProfilesWithEdge.totalCount,
    };
};

export const useMatchedGuestProfiles = ({ cursor, enabled = true }: { cursor: string | null; enabled: boolean }) => {
    const { chainId } = useCurrentEnterprise();
    const { guestProfileApiUrl } = useAppConfiguration();
    const { data, ...props } = useGraphqlQuery(
        guestProfileApiUrl,
        {
            document: MatchedGuestProfilesQueryDocument,
            variables: { chainId, first: 1, after: cursor },
        },
        {
            enabled: !!chainId && enabled,
            staleTime: 0,
        }
    );

    return {
        ...props,
        data: data ? transformToMatchedGuestProfilesWithPagination(data) : undefined,
    };
};
